"use client";

import { useId, useRef, useState, type ReactNode } from "react";
import { CopyIcon } from "@/app/docs/docs-icons";
import { ChatIcon } from "@/app/support/support-icons";
import { startLiveChat } from "@/app/support/live-chat";
import { requestSupport } from "@/app/support/support-prefill";

export const SUPPORT_EMAIL = "support@kleipdata.co.uk";

/* ---------------------------------------------------------------- styling */

type Variant =
  | "primaryOnNavy"
  | "outlineOnNavy"
  | "primaryOnLight"
  | "outlineOnLight"
  | "channel";

function classesFor(variant: Variant) {
  const base =
    "inline-flex items-center justify-center gap-2 rounded-lg text-label-md font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2";
  switch (variant) {
    case "primaryOnNavy":
      return `${base} bg-brand-cyan px-5 py-3 text-brand-navy hover:bg-[#00c2c2] focus-visible:outline-brand-cyan`;
    case "outlineOnNavy":
      return `${base} border border-white/25 px-5 py-3 text-white hover:bg-white/10 focus-visible:outline-brand-cyan`;
    case "primaryOnLight":
      return `${base} bg-brand-navy px-5 py-3 text-white hover:bg-brand-navy-2 focus-visible:outline-brand-electric`;
    case "outlineOnLight":
      return `${base} border border-outline-variant px-5 py-3 text-brand-navy hover:bg-brand-navy/5 focus-visible:outline-brand-electric`;
    case "channel":
      return `${base} border border-outline-variant px-4 py-2 text-sm text-brand-navy hover:border-brand-electric hover:text-brand-electric focus-visible:outline-brand-electric`;
  }
}

/* ------------------------------------------------------------ scroll-to-form */

export function ContactSupportButton({
  variant,
  children,
  subject,
}: {
  variant: Variant;
  children: ReactNode;
  subject?: string;
}) {
  return (
    <button type="button" onClick={() => requestSupport(subject)} className={classesFor(variant)}>
      {children}
    </button>
  );
}

/* --------------------------------------------------------------- live chat */

export function LiveChatButton({
  variant,
  children,
  className,
}: {
  variant: Variant;
  children: ReactNode;
  className?: string;
}) {
  const [unavailable, setUnavailable] = useState(false);
  const noticeId = useId();

  return (
    <span className={`inline-flex flex-col gap-2 ${className ?? ""}`}>
      <button
        type="button"
        aria-label="Start live chat with KleipData support"
        aria-describedby={unavailable ? noticeId : undefined}
        onClick={() => {
          const { connected } = startLiveChat();
          setUnavailable(!connected);
        }}
        className={classesFor(variant)}
      >
        <ChatIcon className="h-4 w-4" />
        {children}
      </button>
      {unavailable && (
        <span
          id={noticeId}
          role="status"
          className={`text-xs leading-5 ${
            variant.endsWith("OnNavy") ? "text-white/70" : "text-on-surface-variant"
          }`}
        >
          Live chat isn&rsquo;t connected in your session yet. Email{" "}
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="font-semibold underline underline-offset-2"
          >
            {SUPPORT_EMAIL}
          </a>{" "}
          and we&rsquo;ll respond within 4 hours.
        </span>
      )}
    </span>
  );
}

/* ------------------------------------------------------------- copy email */

export function CopyEmailButton() {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  async function copy() {
    try {
      await navigator.clipboard.writeText(SUPPORT_EMAIL);
      setCopied(true);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable — the mailto link remains the fallback */
    }
  }

  return (
    <button
      type="button"
      onClick={copy}
      className="inline-flex items-center gap-1.5 rounded-md border border-outline-variant px-2.5 py-1 text-xs font-medium text-brand-navy transition-colors hover:border-brand-electric hover:text-brand-electric focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-electric"
    >
      <CopyIcon className="h-3.5 w-3.5" />
      <span aria-live="polite">
        {copied ? "Email address copied" : "Copy email address"}
      </span>
    </button>
  );
}
